import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, toastError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { LimitsCallout } from "../components/LimitsCallout";
import { ScopePicker, ImportScopeEntry, toCreateJobScope } from "../components/ScopePicker";

/**
 * Step 1 — Import setup.
 *
 * Contact-import only in Milestone D. Renders the scope picker (populated
 * from GET /api/import/my-scopes) and the file-requirements callout. On
 * submit, calls POST /api/import/jobs and navigates to /imports/:jobId
 * (which the wizard shell then places at Step 2 because a freshly-created
 * job's status is RECEIVED, mapped by wizardStepFor to step 2).
 */
export function Step1Setup() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<ImportScopeEntry | null>(null);
  const [busy, setBusy] = useState(false);

  const canSubmit = !!scope && !busy;

  const onCreate = async () => {
    if (!scope) return;
    setBusy(true);
    try {
      const idem = crypto.randomUUID();
      const { data } = await api.post(
        "/import/jobs",
        {
          entity_type: "contact",
          file: {},
          options: {},
          scope: toCreateJobScope(scope),
        },
        { headers: { "Idempotency-Key": idem } },
      );
      navigate(`/imports/${data.id}`);
    } catch (e) {
      toastError(e, "Could not create the import job.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="step-1-setup">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Set up your import</h2>
        <p className="text-sm text-slate-500 mt-1">
          Contact imports only. Pick where these contacts should live and review the CSV requirements before continuing.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Entity</div>
            <div className="text-sm font-medium text-slate-900">Contact</div>
            <div className="text-xs text-slate-500 mt-1">Other entity types will be added in later milestones.</div>
          </div>
          <ScopePicker value={scope} onChange={setScope} />
        </div>
        <LimitsCallout />
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Button
          onClick={onCreate}
          disabled={!canSubmit}
          data-testid="setup-create-btn"
          className="rounded-full"
        >
          {busy ? "Creating…" : "Continue to upload"}
        </Button>
        <span className="text-xs text-slate-500">
          You can cancel any time before commit.
        </span>
      </div>
    </div>
  );
}
